import { AxiosError } from 'axios';
import { FormEvent, useEffect, useState, type ReactNode } from 'react';
import { apiClient } from '../api/client';

type Category = 'pipe' | 'fitting' | 'insulation' | 'valve' | 'other';
type PipeType = 'PEX' | 'PP' | 'common';
type Unit = 'm' | 'pcs';
type Source = 'all' | 'base' | 'own';

type Material = {
  id: string;
  name: string;
  category: Category;
  pipe_type: PipeType;
  unit: Unit;
  is_base: boolean;
};

const CATEGORY_LABELS: Record<Category, string> = {
  pipe: 'труба',
  fitting: 'фитинг',
  insulation: 'утеплитель',
  valve: 'арматура',
  other: 'другое',
};

const UNIT_LABELS: Record<Unit, string> = {
  m: 'м',
  pcs: 'шт',
};

const CATEGORIES = Object.keys(CATEGORY_LABELS) as Category[];
const PIPE_TYPES: PipeType[] = ['PEX', 'PP', 'common'];
const UNITS = Object.keys(UNIT_LABELS) as Unit[];

const SAVE_ERROR_TEXT = 'Не удалось сохранить. Проверьте соединение и повторите.';
const BASE_HINT = 'Базовую позицию менять нельзя';

const headerCellStyle = {
  textAlign: 'left' as const,
  padding: '10px 12px',
  borderBottom: '1px solid #e5e7eb',
  color: '#374151',
  fontWeight: 600,
};

const bodyCellStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'middle' as const,
};

function PencilIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function isTransportError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) {
    return true;
  }

  if (!error.response) {
    return true;
  }

  return error.response.status >= 500;
}

function errorText(error: unknown, fallback: string): string {
  if (!(error instanceof AxiosError)) {
    return fallback;
  }

  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item && typeof item.msg === 'string') {
          return item.msg.trim();
        }

        return '';
      })
      .filter((message) => message.length > 0);

    if (messages.length > 0) {
      return messages.join(' ');
    }
  }

  return fallback;
}

export function MaterialsLibrary() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pipeType, setPipeType] = useState<PipeType | ''>('');
  const [source, setSource] = useState<Source>('all');
  const [items, setItems] = useState<Material[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<Category>('pipe');
  const [formPipeType, setFormPipeType] = useState<PipeType>('PEX');
  const [unit, setUnit] = useState<Unit>('m');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    async function loadMaterials() {
      setIsLoading(true);
      setLoadError(null);

      const params = new URLSearchParams();
      if (search) {
        params.set('search', search);
      }
      if (pipeType) {
        params.set('pipe_type', pipeType);
      }
      params.set('source', source);

      let unauthorized = false;

      try {
        const response = await apiClient.get<{ items?: Material[] }>(
          `/profile/materials?${params.toString()}`,
        );
        if (cancelled) {
          return;
        }

        setItems(Array.isArray(response.data.items) ? response.data.items : []);
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof AxiosError && error.response?.status === 401) {
          unauthorized = true;
          return;
        }

        setLoadError('Не удалось загрузить библиотеку. Проверьте соединение и повторите.');
      } finally {
        if (!cancelled && !unauthorized) {
          setIsLoading(false);
        }
      }
    }

    void loadMaterials();

    return () => {
      cancelled = true;
    };
  }, [search, pipeType, source, reloadKey]);

  function reload() {
    setReloadKey((value) => value + 1);
  }

  function setRowError(id: string, message: string | null) {
    setRowErrors((current) => {
      const next = { ...current };
      if (message) {
        next[id] = message;
      } else {
        delete next[id];
      }
      return next;
    });
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError('Введите название.');
      return;
    }

    setIsSaving(true);
    setFormError(null);
    setActionError(null);

    try {
      await apiClient.post('/profile/materials', {
        name: trimmed,
        category,
        pipe_type: formPipeType,
        unit,
      });
      setName('');
      setCategory('pipe');
      setFormPipeType('PEX');
      setUnit('m');
      setIsFormOpen(false);
      reload();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return;
      }

      if (isTransportError(error)) {
        setActionError(SAVE_ERROR_TEXT);
        return;
      }

      setFormError(errorText(error, 'Сервер отклонил позицию.'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRename(material: Material) {
    const nextName = window.prompt('Новое название', material.name);
    if (nextName === null) {
      return;
    }

    const trimmed = nextName.trim();
    if (!trimmed) {
      setRowError(material.id, 'Введите название.');
      return;
    }

    if (trimmed === material.name) {
      return;
    }

    setActionError(null);
    setRowError(material.id, null);

    try {
      await apiClient.patch(`/profile/materials/${material.id}`, { name: trimmed });
      reload();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return;
      }

      if (isTransportError(error)) {
        setActionError(SAVE_ERROR_TEXT);
        return;
      }

      setRowError(material.id, errorText(error, 'Сервер отклонил название.'));
    }
  }

  async function handleDelete(material: Material) {
    const confirmed = window.confirm(`Удалить «${material.name}»?`);
    if (!confirmed) {
      return;
    }

    setActionError(null);
    setRowError(material.id, null);

    try {
      await apiClient.delete(`/profile/materials/${material.id}`);
      reload();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return;
      }

      if (error instanceof AxiosError && error.response?.status === 422) {
        setRowError(material.id, errorText(error, 'Сервер отклонил удаление.'));
        return;
      }

      if (isTransportError(error)) {
        setActionError(SAVE_ERROR_TEXT);
        return;
      }

      setRowError(material.id, errorText(error, 'Сервер отклонил удаление.'));
    }
  }

  const filtersActive = search.length > 0 || pipeType !== '' || source !== 'all';

  return (
    <section style={{ display: 'grid', gap: '12px' }}>
      <p
        aria-live="polite"
        style={{
          minHeight: '1.25em',
          margin: 0,
          color: '#b91c1c',
        }}
      >
        {actionError ?? ''}
      </p>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="field"
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Поиск по названию"
          aria-label="Поиск по названию"
        />
        <select
          className="field"
          style={{ minWidth: '140px' }}
          value={pipeType}
          onChange={(event) => setPipeType(event.target.value as PipeType | '')}
          aria-label="Тип трубы"
        >
          <option value="">Все типы</option>
          {PIPE_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <select
          className="field"
          style={{ minWidth: '140px' }}
          value={source}
          onChange={(event) => setSource(event.target.value as Source)}
          aria-label="Источник"
        >
          <option value="all">Все источники</option>
          <option value="base">Базовые</option>
          <option value="own">Свои</option>
        </select>
        {isFormOpen ? null : (
          <button type="button" className="btn" onClick={() => setIsFormOpen(true)}>
            Добавить
          </button>
        )}
      </div>

      {isFormOpen ? (
        <form
          onSubmit={handleCreate}
          style={{
            display: 'grid',
            gap: '8px',
            maxWidth: '420px',
            padding: '12px',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            background: '#ffffff',
          }}
        >
          <input
            className="field"
            style={{ minWidth: 0, width: '100%' }}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Название"
            aria-label="Название"
            maxLength={255}
            required
          />
          <select
            className="field"
            style={{ minWidth: 0, width: '100%' }}
            value={category}
            onChange={(event) => setCategory(event.target.value as Category)}
            aria-label="Категория"
          >
            {CATEGORIES.map((item) => (
              <option key={item} value={item}>
                {CATEGORY_LABELS[item]}
              </option>
            ))}
          </select>
          <select
            className="field"
            style={{ minWidth: 0, width: '100%' }}
            value={formPipeType}
            onChange={(event) => setFormPipeType(event.target.value as PipeType)}
            aria-label="Тип трубы новой позиции"
          >
            {PIPE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <select
            className="field"
            style={{ minWidth: 0, width: '100%' }}
            value={unit}
            onChange={(event) => setUnit(event.target.value as Unit)}
            aria-label="Единица"
          >
            {UNITS.map((item) => (
              <option key={item} value={item}>
                {UNIT_LABELS[item]}
              </option>
            ))}
          </select>
          {formError ? (
            <p role="alert" style={{ color: '#b91c1c', margin: 0 }}>
              {formError}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" className="btn" disabled={isSaving}>
              {isSaving ? 'Сохранение…' : 'Добавить'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setIsFormOpen(false);
                setFormError(null);
              }}
            >
              Отмена
            </button>
          </div>
        </form>
      ) : null}

      {isLoading ? <p>Загружаем материалы...</p> : null}

      {!isLoading && loadError ? (
        <p role="alert" style={{ color: '#b91c1c', margin: 0 }}>
          {loadError}
        </p>
      ) : null}

      {!isLoading && !loadError && items.length === 0 ? (
        <p style={{ margin: 0 }}>{filtersActive ? 'Ничего не найдено' : 'Нет позиций'}</p>
      ) : null}

      {!isLoading && !loadError && items.length > 0 ? (
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            background: '#ffffff',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Название</th>
                <th style={headerCellStyle}>Категория</th>
                <th style={headerCellStyle}>Тип трубы</th>
                <th style={headerCellStyle}>Единица</th>
                <th style={headerCellStyle}>Источник</th>
                <th style={headerCellStyle}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((material) => (
                <MaterialRow
                  key={material.id}
                  material={material}
                  rowError={rowErrors[material.id] ?? null}
                  onRename={handleRename}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function LockedAction({
  locked,
  label,
  className,
  onClick,
  children,
  onLockedClick,
}: {
  locked: boolean;
  label: string;
  className: string;
  onClick: () => void;
  children: ReactNode;
  onLockedClick: (point: { x: number; y: number }) => void;
}) {
  if (!locked) {
    return (
      <button type="button" className={className} aria-label={label} title={label} onClick={onClick}>
        {children}
      </button>
    );
  }

  return (
    <span
      title={BASE_HINT}
      style={{ display: 'inline-flex', cursor: 'not-allowed' }}
      onClick={(event) => {
        event.stopPropagation();
        onLockedClick({ x: event.clientX, y: event.clientY });
      }}
    >
      <button
        type="button"
        className={className}
        aria-label={label}
        disabled
        style={{ pointerEvents: 'none' }}
      >
        {children}
      </button>
    </span>
  );
}

function MaterialRow({
  material,
  rowError,
  onRename,
  onDelete,
}: {
  material: Material;
  rowError: string | null;
  onRename: (material: Material) => void;
  onDelete: (material: Material) => void;
}) {
  const errorId = `material-error-${material.id}`;
  const [hintPoint, setHintPoint] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!hintPoint) {
      return;
    }

    const timer = window.setTimeout(() => setHintPoint(null), 2500);
    function hide() {
      setHintPoint(null);
    }

    window.addEventListener('scroll', hide, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', hide, true);
    };
  }, [hintPoint]);

  const hintLeft = hintPoint ? Math.min(hintPoint.x + 12, window.innerWidth - 260) : 0;
  const hintTop = hintPoint ? Math.min(hintPoint.y + 12, window.innerHeight - 48) : 0;

  return (
    <>
      <tr>
        <td style={bodyCellStyle}>{material.name}</td>
        <td style={bodyCellStyle}>{CATEGORY_LABELS[material.category] ?? material.category}</td>
        <td style={bodyCellStyle}>{material.pipe_type}</td>
        <td style={bodyCellStyle}>{UNIT_LABELS[material.unit] ?? material.unit}</td>
        <td style={bodyCellStyle}>{material.is_base ? 'Базовая' : 'Своя'}</td>
        <td style={bodyCellStyle}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <LockedAction
              locked={material.is_base}
              label="Переименовать"
              className="btn btn-icon"
              onClick={() => onRename(material)}
              onLockedClick={setHintPoint}
            >
              <PencilIcon />
            </LockedAction>
            <LockedAction
              locked={material.is_base}
              label="Удалить"
              className="btn btn-icon btn-danger"
              onClick={() => onDelete(material)}
              onLockedClick={setHintPoint}
            >
              <TrashIcon />
            </LockedAction>
          </div>
          {hintPoint ? (
            <span
              role="tooltip"
              style={{
                position: 'fixed',
                left: hintLeft,
                top: hintTop,
                zIndex: 20,
                maxWidth: '240px',
                padding: '6px 8px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: '#ffffff',
                color: '#111827',
                fontSize: '13px',
                boxShadow: '0 4px 12px rgba(17, 24, 39, 0.12)',
              }}
            >
              {BASE_HINT}
            </span>
          ) : null}
        </td>
      </tr>
      {rowError ? (
        <tr>
          <td colSpan={6} style={{ ...bodyCellStyle, paddingTop: 0 }}>
            <p id={errorId} role="alert" style={{ color: '#b91c1c', margin: 0 }}>
              {rowError}
            </p>
          </td>
        </tr>
      ) : null}
    </>
  );
}
